import {
    SHOT_LANDING_LABELS, SHOT_PENALTY_LABELS, allowedPenalties, penaltyStrokes,
} from '@golf/common';
import type { ShotLanding, ShotPenalty } from '@golf/common';
import { ArrowRight, Check, HelpCircle, Target } from 'lucide-react';
import { ICONES_ZONE, ZONES, habitZone } from './PaveZone.tsx';

interface Props {
    trou: number;
    nbTrous: number;
    /** Le rang du coup dans le trou, et le club avec lequel il a été joué. */
    rang: number;
    club: string | null;
    /** Où la balle a atterri. `null` tant que le joueur ne l'a pas dit : c'est
     *  désormais SUR CET ÉCRAN qu'on le désigne, et non sur le pavé. */
    landing: ShotLanding | null;
    penalty: ShotPenalty | null;
    /** Le total du trou après ce coup, pénalités comprises. */
    total: number;
    par: number | null;
    busy?: boolean;
    onCorriger: (landing: ShotLanding) => void;
    onPenalite: (penalty: ShotPenalty | null) => void;
    /** La balle est tombée dedans : le trou se ferme sur ce coup. */
    onRentree: () => void;
    onSuivant: () => void;
}

/* Le bilan d'un coup, plein écran.
 *
 * ── Pourquoi une page entière pour un tap ───────────────────────────────────
 *
 * Le pavé enregistre une ESTIMATION, la balle encore en l'air : « je crois
 * qu'elle est dans le rough ». Ce qui vient après ne se sait qu'une fois
 * arrivé près d'elle — est-elle vraiment dans le rough ou juste derrière, sur
 * le fairway ? est-elle jouable ? est-elle tombée dedans ?
 *
 * Ces questions n'avaient pas leur place sur le pavé : elles y ajoutaient des
 * bascules et des seconds boutons qu'il fallait armer AVANT de savoir. Elles
 * ont leur place ici, sur l'écran qu'on regarde en marchant vers sa balle, où
 * il n'y a rien d'autre à faire et où l'on a la place de tout montrer.
 *
 * ── Le tiers du haut porte le lie ───────────────────────────────────────────
 *
 * Pas un bandeau décoratif : c'est la réponse à « où est ma balle ? », donnée
 * par la couleur avant d'être lue. Le joueur qui rallume son écran trois
 * minutes plus tard la retrouve d'un coup d'œil, sans lire un mot.
 *
 * Le numéro du trou est écrit en grand par-dessus, parce que c'est la seule
 * chose qu'on cherche en revenant à l'écran après avoir marché.
 *
 * ── Sortir par le bas ───────────────────────────────────────────────────────
 *
 * Un seul bouton plein : « Coup suivant ». Tout le reste est un ajustement,
 * pas une étape — on peut n'y toucher à rien. Sauf si la balle est rentrée :
 * il n'y a alors pas de coup suivant, et c'est le trou qui se ferme.
 */
export function BilanCoup({
    trou, nbTrous, rang, club, landing, penalty, total, par,
    busy = false, onCorriger, onPenalite, onRentree, onSuivant,
}: Props) {
    const motifs = landing == null ? [] : allowedPenalties(landing);
    /* Sans zone, l'entête reste sur le vert de scène : il ne peut pas montrer
       un lie qu'on n'a pas encore choisi, et en inventer un — le fairway par
       défaut, comme avant — revenait à répondre à la place du joueur. */
    const habit = landing == null
        ? { backgroundColor: 'var(--fairway-deep)', color: '#fff', border: 'none' }
        : habitZone(landing);

    return (
        /* Trois bandes, et une seule défile.
         *
         * L'écran entier défilait : sur un 360×740, le bouton de sortie tombait
         * à 35 px sous le pli et il fallait pousser la page pour l'atteindre.
         * On peut rogner des pixels ici et là, mais la hauteur des téléphones
         * n'a pas de plancher — c'est la structure qu'il faut changer.
         *
         * L'en-tête tient son tiers, la barre d'action reste collée en bas, et
         * seules les corrections défilent entre les deux. Le geste de sortie est
         * alors sous le pouce quelle que soit la taille de l'écran, ce qui est
         * la seule garantie qui vaille. */
        <div className="fixed inset-0 z-50 flex flex-col bg-base-100">
            {/* ── Le tiers du haut : le lie, en grand ─────────────────────── */}
            {/* Un tiers de l'écran, mesuré et non estimé : `33dvh` de hauteur
                minimale. `dvh` plutôt que `vh` parce que la barre d'URL se
                rétracte sur mobile et que `vh` reste figé sur la hauteur
                dépliée.

                Chaque zone porte SA matière, pas une texture commune teintée :
                le grain fin du green tondu à ras, le sable irrégulier du
                bunker, les brins hauts et désordonnés du rough, la houle
                horizontale de l'eau, les bandes de tonte du fairway et le ruban
                de chantier du hors-limites. Voir `.texture-*` dans index.css.

                Le joueur reconnaît alors la matière avant même la couleur, ce
                qui compte pour les deux zones claires : green et bunker se
                distinguent mal à la teinte seule sous un écran au soleil.

                Le trait du bas n'est pas décoratif : il détache le panneau du
                papier en dessous, qui est presque aussi clair que le green ou
                le bunker. */}
            <header
                className={`texture-${landing} flex min-h-[33dvh] shrink-0 flex-col items-center
                            justify-center gap-2 px-6 pb-6 pt-[max(1.5rem,env(safe-area-inset-top))]`}
                style={{
                    /* La couleur ici, le motif dans la classe : une seule
                       source pour la teinte, et six matières par-dessus. */
                    backgroundColor: habit.backgroundColor,
                    color: habit.color,
                    borderBottom: `2px solid color-mix(in oklab, ${habit.backgroundColor} 68%, black)`,
                }}
            >
                <p className="font-score text-sm font-bold uppercase tracking-[0.2em] opacity-80">
                    Trou {trou}/{nbTrous}
                </p>
                {/* Le médaillon reprend la forme du bilan de trou : un disque,
                    une icône, et le nom en dessous. Deux écrans de fin de geste
                    qui se ressemblent, c'est un seul écran à apprendre. */}
                <span
                    className="flex size-24 items-center justify-center rounded-full shadow-lg [&_svg]:size-11"
                    style={{
                        background: 'color-mix(in oklab, currentColor 14%, transparent)',
                        border: '2px solid color-mix(in oklab, currentColor 30%, transparent)',
                    }}
                >
                    {/* Un point d'interrogation tant que la zone n'est pas
                        dite : le médaillon garde sa place et sa forme, seul son
                        contenu attend. */}
                    {landing == null
                        ? <HelpCircle aria-hidden="true" />
                        : ICONES_ZONE[landing]}
                </span>
                <h1 className="font-display text-3xl font-bold leading-none tracking-tight">
                    {landing == null ? 'Où est la balle ?' : SHOT_LANDING_LABELS[landing]}
                </h1>
                {/* Tout ce qui situe le coup tient sur une ligne : son rang, le
                    club, ce qu'il a coûté, et le total du trou. C'était trois
                    blocs empilés, dont un que le pouce recouvrait. */}
                <p className="text-center text-sm font-bold">
                    Coup {rang}
                    {club != null && <span className="opacity-70">{' · '}{club}</span>}
                    {penalty != null && (
                        <span className="opacity-70">
                            {' · '}+{penaltyStrokes(penalty)} de pénalité
                        </span>
                    )}
                    <span className="block opacity-70">
                        {total} coup{total > 1 ? 's' : ''} sur ce trou
                        {par != null && ` · par ${par}`}
                    </span>
                </p>
            </header>

            <div className="mx-auto flex w-full max-w-md flex-1 flex-col gap-3 overflow-y-auto px-4 py-4 text-base-content">

                {/* ── Corriger le lie ─────────────────────────────────────── */}
                <section>
                    <h2 className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em]"
                        style={{ color: 'var(--tuile-encre)' }}>
                        {landing == null ? 'Où est-elle tombée ?' : "Ce n'est pas là ?"}
                    </h2>
                    {/* Les six zones, dans l'ordre du pavé. Un ordre différent
                        ici et le joueur chercherait : ce sont les mêmes boutons,
                        au même rang, avec les mêmes couleurs.

                        Sans la matière, en revanche : à trois par rangée, un
                        grain de sable ou une houle deviennent du bruit sous un
                        libellé de onze pixels. Le plein écran juste au-dessus
                        la porte déjà, et c'est là qu'elle se lit. */}
                    <div className="grid grid-cols-3 gap-2">
                        {ZONES.map((z) => {
                            const actif = z === landing;
                            return (
                                <button key={z} type="button" disabled={busy}
                                    onClick={() => onCorriger(z)}
                                    aria-pressed={actif}
                                    style={habitZone(z)}
                                    className={`flex flex-col items-center justify-center gap-0.5 rounded-field
                                                px-1 py-2 text-xs font-bold shadow-sm transition-transform
                                                active:scale-95 disabled:opacity-40 ${
                                        actif
                                            ? 'ring-2 ring-accent ring-offset-2 ring-offset-base-100'
                                            /* En retrait SEULEMENT s'il y a déjà une
                                               réponse à corriger. Tant qu'aucune zone
                                               n'est dite, les six sont à égalité : les
                                               pâlir toutes les faisait passer pour
                                               désactivées, alors qu'elles portent la
                                               question principale de l'écran. */
                                            : landing == null ? '' : 'opacity-55'
                                    }`}>
                                    <span className="[&_svg]:size-5">{ICONES_ZONE[z]}</span>
                                    {SHOT_LANDING_LABELS[z]}
                                </button>
                            );
                        })}
                    </div>
                </section>

                {/* ── La pénalité ─────────────────────────────────────────── */}
                {motifs.length > 0 && (
                    <section>
                        <h2 className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em]"
                            style={{ color: 'var(--tuile-encre)' }}>
                            Pénalité
                        </h2>
                        <div className="flex flex-col gap-2">
                            {/* « Aucune » d'abord : c'est le cas qu'on veut
                                pouvoir choisir vite quand la pénalité posée
                                d'office ne s'applique pas — une balle jouée
                                telle quelle dans l'obstacle, par exemple.
                                Le hors-limites n'offre pas ce choix : la règle
                                18 est sans alternative, et la ligne « aucune »
                                y laisserait croire le contraire. */}
                            {landing !== 'out_of_bounds' && (
                                <Motif actif={penalty == null} busy={busy}
                                    onClick={() => onPenalite(null)}
                                    titre="Aucune pénalité"
                                    detail={landing === 'penalty'
                                        ? 'Balle jouée telle quelle dans l’obstacle'
                                        : 'La balle est jouable'} />
                            )}
                            {motifs.map((m) => (
                                <Motif key={m} actif={penalty === m} busy={busy}
                                    onClick={() => onPenalite(m)}
                                    titre={`+${penaltyStrokes(m)} — ${etiquetteCourte(m)}`}
                                    detail={SHOT_PENALTY_LABELS[m]} />
                            ))}
                        </div>
                    </section>
                )}

            </div>

            {/* ── Sortir, toujours à portée de pouce ──────────────────────── */}
            <div className="safe-bottom mx-auto w-full max-w-md shrink-0 border-t border-base-300 bg-base-100 px-4 pt-3">
                <div className="flex flex-col gap-2">
                    {/* « Coup suivant » EN PREMIER, et en grand.
                        C'est ce qui arrive presque toujours : la balle est
                        quelque part, on va la rejouer. Il mène au choix du club,
                        qui est la question suivante.

                        « La balle est rentrée » passe DESSOUS et plus petit. Elle
                        rentre rarement quand on n'a pas le putter en main, et un
                        bouton de la même taille suggérait deux issues aussi
                        probables l'une que l'autre. Il garde le laiton — la
                        couleur de ce qui est acquis — et son point
                        d'exclamation, seul de toute l'interface : la taille dit
                        la fréquence, la couleur dit la bonne nouvelle. */}
                    <button type="button" disabled={busy} onClick={onSuivant}
                        className="btn btn-primary h-16 w-full gap-3 text-xl font-bold shadow-md">
                        Coup suivant
                        <ArrowRight size={24} strokeWidth={2.5} aria-hidden="true" />
                    </button>
                    <button type="button" disabled={busy} onClick={onRentree}
                        className="btn btn-accent btn-sm w-full gap-2 font-bold">
                        <Target size={16} aria-hidden="true" />
                        La balle est rentrée&nbsp;!
                    </button>
                </div>
            </div>
        </div>
    );
}

/** Une ligne de choix de pénalité : le coût en tête, la règle en dessous.
 *
 *  Le nom de la règle et non seulement le chiffre : « +1 » sans motif est un
 *  chiffre qu'on ne sait plus expliquer trois trous plus loin. */
function Motif({ actif, busy, onClick, titre, detail }: {
    actif: boolean; busy: boolean; onClick: () => void; titre: string; detail: string;
}) {
    return (
        <button type="button" disabled={busy} onClick={onClick} aria-pressed={actif}
            className={`flex items-center gap-3 rounded-field px-3 py-2 text-left transition-colors ${
                actif ? 'bg-accent text-accent-content' : 'bg-base-200 hover:bg-base-300'
            }`}>
            <span aria-hidden="true"
                className={`flex size-5 shrink-0 items-center justify-center rounded-full border-2 ${
                    actif ? 'border-accent-content' : 'border-current opacity-40'}`}>
                {actif && <Check size={12} strokeWidth={4} />}
            </span>
            <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold leading-tight">{titre}</span>
                <span className={`block text-xs leading-tight ${actif ? 'opacity-75' : 'opacity-60'}`}>
                    {detail}
                </span>
            </span>
        </button>
    );
}

/** Le motif en deux mots, pour la ligne de titre. Le texte long de
 *  `SHOT_PENALTY_LABELS` vit juste en dessous. */
function etiquetteCourte(m: ShotPenalty): string {
    if (m === 'relief') return 'dégagement';
    if (m === 'stroke_and_distance') return 'coup et distance';
    return 'balle injouable';
}
